import { ArrowRightLeft, Car } from 'lucide-react'
import { resolveStripDensity } from '../model/blockDensity'
import type { TravelKind } from '../model/blockView'
import { BlockSegment, textFor } from './BlockSegment'

/**
 * The Anfahrt / Abfahrt band above and below the work.
 *
 * The icon tier is the part that earns its place on a phone. A ten-minute drive is 16px of grid and
 * cannot hold the word `Anfahrt` at any size worth reading — but it holds a car at 10px, so driving
 * stays distinguishable from working without the worker having to know what `#6574CC` means. Below
 * that the strip is a bare band of colour, which is still true: it says a drive is there, at any
 * height down to a pixel.
 */

export type { TravelKind }

interface Props {
  kind: TravelKind
  color: string
  /** From `distributeSegmentHeights` — already the strip's real share of the block. */
  heightPx: number
  divided?: boolean
  /** The one-day view: larger type, and the drive's length beside the word. */
  wide?: boolean
  /**
   * The drive's length, shown at the right of a labelled strip. Only with `wide`: a three-day
   * column has no width to spare for a number beside the word.
   */
  minutes?: number
}

export const TravelStrip = ({
  kind,
  color,
  heightPx,
  divided = false,
  wide = false,
  minutes,
}: Props) => {
  if (heightPx <= 0) return null

  const density = resolveStripDensity(heightPx, wide)
  const text = textFor(wide)
  // A transfer is a drive straight from the last job to this one, not a trip out and back — its own
  // icon, because that is the difference the worker plans the morning around.
  const Icon = kind === 'Transfer' ? ArrowRightLeft : Car

  if (density === 'bare') {
    return <BlockSegment color={color} heightPx={heightPx} divided={divided} />
  }

  return (
    <BlockSegment
      color={color}
      heightPx={heightPx}
      divided={divided}
      padded={density === 'label'}
    >
      <span className="flex min-w-0 items-center gap-1">
        <Icon
          className={
            wide
              ? density === 'label'
                ? 'h-3.5 w-3.5 shrink-0'
                : 'h-3 w-3 shrink-0'
              : density === 'label'
                ? 'h-3 w-3 shrink-0'
                : 'h-2.5 w-2.5 shrink-0'
          }
          strokeWidth={2.5}
          aria-hidden
        />
        {density === 'label' && <span className={text.strip}>{kind}</span>}
        {density === 'label' && wide && minutes != null && minutes > 0 && (
          <span className={`${text.meta} ml-auto shrink-0 tabular-nums`}>{minutes} Min</span>
        )}
      </span>
    </BlockSegment>
  )
}
